import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { getAlerts, getUnreadCount, markAlertAsRead, markAllAsRead as markAllAsReadService, dismissAlert as dismissAlertService } from "../services/alerts/LeadAlertService";
import { useApp } from "../hooks/useApp";

const AlertContext = createContext();

export const AlertProvider = ({ children }) => {
  const { authInfo } = useApp();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const intervalRef = useRef(null);

  const userId = authInfo?.id;
  const rolId = authInfo?.userRole?.id;

  const refreshCount = useCallback(async () => {
    if (!userId || !rolId) return;
    try {
      const count = await getUnreadCount(userId, rolId);
      setUnreadCount(count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, [userId, rolId]);

  const loadAlerts = useCallback(async (pageNum = 0) => {
    if (!userId || !rolId) return;
    setLoading(true);
    try {
      const data = await getAlerts(userId, rolId, pageNum, 10);
      if (pageNum === 0) {
        setAlerts(data.content || []);
      } else {
        setAlerts(prev => [...prev, ...(data.content || [])]);
      }
      setPage(pageNum);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error("Error loading alerts:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, rolId]);

  const loadMore = useCallback(() => {
    if (page + 1 < totalPages) {
      loadAlerts(page + 1);
    }
  }, [page, totalPages, loadAlerts]);

  const markAsRead = useCallback(async (alertId) => {
    try {
      await markAlertAsRead(alertId);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, isRead: true } : a));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking alert as read:", error);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    if (!userId || !rolId) return;
    try {
      await markAllAsReadService(userId, rolId);
      setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  }, [userId, rolId]);

  const dismiss = useCallback(async (alertId) => {
    try {
      await dismissAlertService(alertId);
      setAlerts(prev => {
        const alert = prev.find(a => a.id === alertId);
        if (alert && !alert.isRead) {
          setUnreadCount(c => Math.max(0, c - 1));
        }
        return prev.filter(a => a.id !== alertId);
      });
    } catch (error) {
      console.error("Error dismissing alert:", error);
    }
  }, []);

  // Polling cada 60 segundos
  useEffect(() => {
    if (!userId || !rolId) return;

    refreshCount();

    intervalRef.current = setInterval(() => {
      refreshCount();
    }, 60000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [userId, rolId, refreshCount]);

  const value = useMemo(() => ({
    alerts,
    unreadCount,
    loading,
    page,
    totalPages,
    loadAlerts,
    loadMore,
    markAsRead,
    markAllRead,
    dismiss,
    refreshCount,
  }), [alerts, unreadCount, loading, page, totalPages, loadAlerts, loadMore, markAsRead, markAllRead, dismiss, refreshCount]);

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlerts must be used within an AlertProvider");
  }
  return context;
};

export default AlertContext;
